import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ptBrLocale from '@fullcalendar/core/locales/pt-br';
import { Dropdown } from 'primereact/dropdown';
import { Card } from 'primereact/card';
import { AuthContext } from '../contexts/AuthContext';
import api from '../api/api';
import { FormAgenda } from './FormAgenda';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { FormTurmaAgenda } from './FormTurmaAgenda';
import { Dialog } from "primereact/dialog";

function FullCalendarApp() {
  const toast = useRef(null);
  const { user } = useContext(AuthContext);

  const [events, setEvents] = useState([]);
  const [instrutores, setInstrutores] = useState([]);
  const [instrutorSelected, setInstrutorSelected] = useState(null);
  const [salas, setSalas] = useState([]);
  const [salaSelected, setSalaSelected] = useState(null);
  const [agendaNome, setAgendaNome] = useState("Agenda Semanal");

  const [agenda, setAgenda] = useState({});
  const [showAgenda, setShowAgenda] = useState(false);
  const [showDeleteAgenda, setShowDeleteAgenda] = useState(false);
  const [loadingAgenda, setLoadingAgenda] = useState(false);
  const [atualizarAgenda, setAtualizarAgenda] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [semPermissao, setSemPermissao] = useState(false);

  const [turmas, setTurmas] = useState({});
  const [loadingTurma, setLoadingTurma] = useState(false);
  const [showTurma, setShowTurma] = useState(false);

  // ─── Funções de carregamento ───────────────────────────────────────────

  const carregarAgendaPorInstrutor = useCallback(async (instrutorId) => {
    if (!instrutorId) return;
    try {
      setLoadingAgenda(true);
      const response = await api.get(`/turma/agenda/${instrutorId}`);

      setEvents(response.data || []);
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro ao carregar agenda do instrutor',
        detail: err.message || 'Falha na requisição',
        life: 5000,
      });
    } finally {
      setLoadingAgenda(false);
    }
  }, []);

  const carregarAgendaPorSala = useCallback(async (salaId) => {
    if (!salaId) return;
    try {
      setLoadingAgenda(true);
      const anoAtual = new Date().getFullYear();
      const response = await api.get(`/turma/agendaPorSala/${salaId}/${anoAtual}`);
      console.log('Agenda por sala:', response.data);
      setEvents(response.data || []);
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro ao carregar agenda por sala',
        detail: err.message || 'Falha na requisição',
        life: 5000,
      });
    } finally {
      setLoadingAgenda(false);
    }
  }, []);

  // ─── Inicialização ─────────────────────────────────────────────────────

  useEffect(() => {
    async function inicializar() {
      try {
        const resInstrutores = await api.get(`/perfil/instrutor`);
        const instrutoresDisponiveis = resInstrutores.data || [];

        if (user?.perfilUser?.[0] === 'user-instrutor') {
          const resMeuInstrutor = await api.get(`/perfil/instrutor/${user.email}`);
          const meuInstrutor = resMeuInstrutor.data;
          setInstrutores([meuInstrutor]);
          setInstrutorSelected(meuInstrutor.id);
        } else {
          setInstrutores(instrutoresDisponiveis);
        }

        const resSalas = await api.get('/turma/listSalas');
        setSalas(resSalas.data || []);
      } catch (err) {
        toast.current?.show({
          severity: 'error',
          summary: 'Erro ao inicializar calendário',
          detail: err.message,
          life: 5000,
        });
      }
    }

    inicializar();
  }, [user]);

  // ─── Lógica de recarregamento (exclusiva: sala OU instrutor) ──────────

  useEffect(() => {
    // Limpa antes de decidir qual carregar
    setEvents([]);

    if (salaSelected) {
      // Sala tem prioridade → ignora instrutor
      carregarAgendaPorSala(salaSelected);
    } else if (instrutorSelected) {
      // Só instrutor (sem sala selecionada)
      carregarAgendaPorInstrutor(instrutorSelected);
    }
    // Se nenhum dos dois → fica vazio (já setamos setEvents([]) acima)
  }, [
    salaSelected,
    instrutorSelected,
    atualizarAgenda,
    carregarAgendaPorSala,
    carregarAgendaPorInstrutor
  ]);

  // ─── Handlers (mantidos iguais) ────────────────────────────────────────

  const handleDeleteClick = (data) => {
    data.userEmail = user?.email;

    confirmDialog({
      message: `Você tem certeza que quer remover essa turma?`,
      header: 'Confirmação',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: async () => {
        try {
          await api.delete(`turma/${data.id}`);
          toast.current?.show({
            severity: 'success',
            summary: 'Remoção concluída!',
            detail: 'Turma removida com sucesso',
            life: 5000,
          });
          setAtualizarAgenda(prev => prev + 1);
        } catch (error) {
          toast.current?.show({
            severity: 'error',
            summary: 'Erro ao remover turma',
            detail: error.response?.data || error.message,
            life: 5000,
          });
        }
      },
    });
  };

  const handleAdicionarClick = () => {
    setIsEditing(false);
    setAgenda({
      diaSemana: '',
      diaSemanas: ['DOMINGO', 'SEGUNDA', 'TERÇA', 'QUARTA', 'QUINTA', 'SEXTA', 'SÁBADO'],
      hora: '',
      qtdeHorarios: 1,
      receptorId: '',
      emissorId: '',
      titulo: '',
      semanal: '',
      data: '',
      color: '',
      exibirAte: '',
      sala: '',
    });
    setShowAgenda(true);
  };

  const handleEventClick = async (e) => {
    try {
      const idPerfilResp = await api.get(`/perfil/instrutor/${user.email}`);
      const idPerfil = idPerfilResp.data.id;

      setLoadingAgenda(true);

      if (user.perfilUser[0] === "user-instrutor" && idPerfil !== e.extendedProps.emissorId) {
        setSemPermissao(true);
        return;
      }

      if (e.extendedProps.tipo === "A") {
        const agendaResp = await api.get(`/agenda/${e.id}`);
        const agendaData = agendaResp.data;

        confirmDialog({
          message: `Deseja editar ou deletar o planejamento?`,
          header: "Confirmação",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "Editar",
          rejectLabel: "Deletar",
          accept: async () => {
            setIsEditing(true);
            const hora = agendaData.hora.toString();
            const horaFormatada = hora.substring(0, hora.length - 3);

            setAgenda({
              id: agendaData.id,
              diaSemana: agendaData.diaSemana,
              diaSemanas: ["DOMINGO", "SEGUNDA", "TERÇA", "QUARTA", "QUINTA", "SEXTA", "SÁBADO"],
              hora: horaFormatada,
              qtdeHorarios: agendaData.qtdeHorarios,
              receptorId: agendaData.receptorId,
              emissorId: agendaData.emissorId,
              titulo: agendaData.titulo,
              semanal: agendaData.semanal,
              data: agendaData.data,
              color: agendaData.color,
              exibirAte: agendaData.exibirAte,
              sala: agendaData.sala
            });
            setShowAgenda(true);
          },
          reject: () => {
            const hora = agendaData.hora.toString();
            const horaFormatada = hora.substring(0, hora.length - 3);

            setAgenda({
              id: agendaData.id,
              diaSemana: agendaData.diaSemana,
              diaSemanas: ["DOMINGO", "SEGUNDA", "TERÇA", "QUARTA", "QUINTA", "SEXTA", "SÁBADO"],
              hora: horaFormatada,
              qtdeHorarios: agendaData.qtdeHorarios,
              receptorId: agendaData.receptorId,
              receptorNome: agendaData.receptorNome,
              emissorId: agendaData.emissorId,
              emissorEmail: agendaData.emissorEmail,
              titulo: agendaData.titulo,
              semanal: agendaData.semanal,
              data: agendaData.data,
              color: agendaData.color,
              exibirAte: agendaData.exibirAte,
              sala: agendaData.sala
            });
            setShowDeleteAgenda(true);
          },
        });
      } else {
        if (user.perfilUser[0] !== "user-instrutor") {
          confirmDialog({
            message: `Deseja editar ou deletar a turma?`,
            header: "Confirmação",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Editar",
            rejectLabel: "Deletar",
            accept: async () => {
              setLoadingTurma(true);
              const response = await api.get(`turma/${e.id}`);
              const turmaData = response.data;
              const hora = turmaData.hora.toString();
              const horaFormatada = hora.substring(0, hora.length - 3);

              setTurmas({
                id: turmaData.id,
                ano: turmaData.ano,
                diaSemana: turmaData.diaSemana,
                diaSemanas: ["DOMINGO", "SEGUNDA", "TERÇA", "QUARTA", "QUINTA", "SEXTA", "SÁBADO"],
                hora: horaFormatada,
                qtdeVagas: turmaData.qtdeVagas,
                qtdeHorarios: turmaData.qtdeHorarios,
                sala: turmaData.sala,
                cursoId: turmaData.cursoId,
                perfilId: turmaData.perfilId,
                exibirAte: turmaData.exibirAte,
                sala: turmaData.sala
              });
              setShowTurma(true);
              setLoadingTurma(false);
            },
            reject: () => {
              const data = { id: e.id, userEmail: "" };

              if (e._def.ui.backgroundColor === "#EB4D39") {
                handleDeleteClick(data);
              } else {
                toast.current?.show({
                  life: 5000,
                  severity: "error",
                  summary: "Erro ao remover a turma",
                  detail: "Turma contém alunos, não é permitido deletar",
                });
              }
            },
          });
        }
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro ao processar evento',
        detail: err.message,
        life: 5000,
      });
    } finally {
      setLoadingAgenda(false);
    }
  };

  return (
    <>
      <FormTurmaAgenda
        defaultValues={turmas}
        visible={showTurma}
        setVisible={setShowTurma}
        isLoading={loadingTurma}
        setAtualizarAgenda={setAtualizarAgenda}
      />

      <Dialog
        header="Sem permissão"
        onHide={() => setSemPermissao(false)}
        visible={semPermissao}
        breakpoints={{ '960px': '75vw', '640px': '100vw' }}
        style={{ width: '50vw' }}
      >
        O usuário atual não tem permissão para editar/deletar esse planejamento!
      </Dialog>

      <Toast ref={toast} position="bottom-right" />

      <FormAgenda
        defaultValues={agenda}
        visible={showAgenda}
        setVisible={setShowAgenda}
        visibleDelete={showDeleteAgenda}
        setVisibleDelete={setShowDeleteAgenda}
        isLoading={loadingAgenda}
        setAtualizarAgenda={setAtualizarAgenda}
        isEditing={isEditing}
      />

      <Card title={agendaNome}>

        <div className="p-fluid p-formgrid p-grid">

          <div className="p-field p-col-12 p-md-4">
            <label htmlFor="instrutor">Instrutor</label>
            <Dropdown
              id="instrutor"
              options={instrutores}
              value={instrutorSelected}
              onChange={(e) => {
                setInstrutorSelected(e.value);
                if (e.value) setSalaSelected(null);
              }}
              optionLabel="nome"
              optionValue="id"
              placeholder="Selecione um Instrutor"
              filter
              showClear
              disabled={user?.perfilUser?.[0] === 'user-instrutor'}
            />
          </div>

          <div className="p-field p-col-12 p-md-4">
            <label htmlFor="sala">Sala</label>
            <Dropdown
              id="sala"
              options={salas}
              value={salaSelected}
              onChange={(e) => {
                setSalaSelected(e.value);
                if (e.value) setInstrutorSelected(null);
              }}
              optionLabel="nome"
              optionValue="id"
              placeholder="Selecione uma Sala"
              filter
              showClear
            />
          </div>

          <div className="p-field p-col-12 p-md-2">
            <label className="p-d-block p-mb-2" style={{ visibility: 'hidden' }}>
              Adicionar
            </label>

            <Button
              label="Adicionar"
              icon="pi pi-plus"
              className="p-button-primary w-full"
              onClick={handleAdicionarClick}
            />
          </div>



        </div>


        {!salaSelected && !instrutorSelected && (
          <div className="p-mt-4 p-text-center" style={{ color: '#666', fontStyle: 'italic' }}>
            Selecione um instrutor ou uma sala para visualizar a agenda
          </div>
        )}

        <div className="App mt-4">
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            firstDay={0}
            slotMinTime="07:00"
            slotMaxTime="22:00"
            events={events}
            nowIndicator
            eventClick={(info) => handleEventClick(info.event)}
            datesSet={(dateInfo) => {
              const view = dateInfo.view.type;
              if (view === 'timeGridWeek') setAgendaNome('Agenda Semanal');
              else if (view === 'timeGridDay') setAgendaNome('Agenda Diária');
              else if (view === 'dayGridMonth') setAgendaNome('Agenda Mensal');
              else setAgendaNome('');
            }}
            locale={ptBrLocale}
            height="auto"
          />
        </div>
      </Card>
    </>
  );
}

export default FullCalendarApp;